import { easeInOut, motion } from 'motion/react'
import { BarChart3, ClipboardList, TrendingUp, Users } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'

const floatingAnimation = {
  initial: { y: 0 },
  animate: {
    y: [-10, 10, -10],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

const glowAnimation = {
  initial: { opacity: 0.3 },
  animate: {
    opacity: [0.3, 0.6, 0.3],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

/*
  One measured figure, three statements about what the product does.
  The 85% time saved, 20+ hours, 25% cost reduction and 10+ active teams that
  stood here were not measured anywhere; the audience for this page assesses
  evidence for a living and reads an unsourced percentage as one.

  The 48% is from internal testing across three client projects, and it is
  written with that population attached. A percentage without its denominator
  is the same claim with the checkable part removed.

  The other three describe workflow steps that exist: WorkflowStepId.PicoTables,
  WorkflowStepId.Deduplication and WorkflowStepId.Prisma.
*/
const results = [
  {
    icon: TrendingUp,
    metric: '48%',
    label: 'Less time per review',
    description: 'Measured in internal testing across three client projects',
  },
  {
    icon: ClipboardList,
    metric: 'PICO',
    label: 'Questions built first',
    description: 'PICO tables are a step in the workflow, before any query runs',
  },
  {
    icon: BarChart3,
    metric: 'PRISMA',
    label: 'Counts that reconcile',
    description: 'Every count recorded as you screen, not reassembled at the end',
  },
  {
    icon: Users,
    metric: 'Beta',
    label: 'Private programme',
    description: 'Open to a small number of teams while we build alongside them',
  },
]

export const ResultsSection = () => (
  <div className="relative overflow-hidden bg-[#001871] font-roboto text-white">
    {/* Enhanced texture overlay */}
    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light"></div>
    <div className="absolute inset-0 bg-linear-to-b from-[#001871] via-[#001871] to-[#001871]/90"></div>

    {/* Abstract shapes with improved contrast */}
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 0.15, scale: 1 }}
      transition={{ duration: 2 }}
      className="absolute left-20 top-20 size-96 rounded-full bg-white blur-3xl"
    />
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 0.15, scale: 1 }}
      transition={{ duration: 2, delay: 0.5 }}
      className="absolute bottom-20 right-20 size-96 rounded-full bg-white blur-3xl"
    />

    {/* Floating elements with improved visibility */}
    <motion.div
      variants={floatingAnimation}
      initial="initial"
      animate="animate"
      className="absolute left-20 top-40 hidden size-20 rounded-2xl bg-white/20 shadow-lg backdrop-blur-lg lg:block"
    />
    <motion.div
      variants={floatingAnimation}
      initial="initial"
      animate="animate"
      transition={{ delay: 1 }}
      className="absolute bottom-40 right-20 hidden size-16 rounded-full bg-white/20 shadow-lg backdrop-blur-lg lg:block"
    />

    <div className="relative py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="space-y-8"
          >
            <motion.div
              variants={glowAnimation}
              initial="initial"
              animate="animate"
              className="inline-flex items-center rounded-full bg-white/20 px-6 py-2 text-sm font-medium text-white shadow-lg backdrop-blur-xs"
            >
              Measured in internal testing
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
              className="text-4xl font-bold tracking-tight text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-5xl"
            >
              What we have measured so far
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
              className="text-xl leading-8 text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-2xl"
            >
              SOTA Navigator is in private beta. This is what internal testing showed, and the
              workflow that produced it.
            </motion.p>
          </motion.div>
        </div>

        <motion.div
          className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
        >
          {results.map((item, index) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="border border-white/20 bg-white/10 backdrop-blur-lg transition-all duration-300 hover:shadow-2xl">
                <CardContent className="p-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-6 rounded-xl bg-white/20 p-3">
                      <item.icon className="size-8 text-white" />
                    </div>
                    <motion.div
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: index * 0.2 + 0.5, duration: 0.5 }}
                      className="mb-2 text-4xl font-bold text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)]"
                    >
                      {item.metric}
                    </motion.div>
                    <div className="mb-2 font-medium text-white/90">{item.label}</div>
                    <p className="text-sm text-white/70">{item.description}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  </div>
)
